import { ArrowRight, ShoppingBag, ShieldCheck, Truck, Wrench, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { CONTACT, SERVICES, SHORT_NAMES, STORE_URL } from "@/data/site";

const PERKS = [
  {
    icon: ShoppingBag,
    title: "Order Online, Anytime",
    text: "Browse our full accessories catalogue and place your order 24/7 through our secure online store.",
  },
  {
    icon: ShieldCheck,
    title: "Genuine Parts, Billed",
    text: "Every purchase comes with a proper bill through our billing partner — no grey-market components.",
  },
  {
    icon: Wrench,
    title: "Free Expert Fitting",
    text: "Buy online and get it fitted by our master technicians at the NH.83 garage, Palani.",
  },
];

export default function Store() {
  return (
    <main data-testid="store-page" className="pt-16">
      <Seo
        title="Online Store — Bowegans Car Care, Palani"
        siteName="Bowegans Car Care"
        description="Shop car accessories online from Bowegans Car Care, Palani — alloy wheels, seat covers, audio systems, lighting, sunfilm, PPF and more. Order online, fitted at our garage."
      />
      <section className="border-b border-white/10 bg-[#0E1013]">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6">
          <SectionHeading
            testid="store-page-heading"
            eyebrow="Online store"
            title="Shop online. Fitted here."
            description="Our complete accessories range is now a click away. Order through our secure online store and drive in for a free expert fitting."
          />
          <Reveal delay={0.25} className="mt-10">
            <a
              href={STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="store-visit-button"
              className="font-mono2 group inline-flex items-center gap-3 bg-[#3B82F6] px-8 py-4 text-sm font-bold uppercase tracking-[0.25em] text-white transition-colors hover:bg-[#2563EB]"
            >
              Visit Online Store
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </a>
            <p className="font-mono2 mt-4 text-[10px] uppercase tracking-[0.25em] text-gray-600">
              Opens our secure store in a new tab
            </p>
          </Reveal>
        </div>
      </section>
      <Marquee />

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6" data-testid="store-categories-section">
        <SectionHeading
          testid="store-categories-heading"
          eyebrow="What you can order"
          title="Every category we fit, online"
        />
        <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="store-categories-grid">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={(i % 4) * 0.08} className="h-full">
              <a
                href={STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`store-category-${s.id}`}
                className="spotlight-card group flex h-full flex-col justify-between border border-[#262A35] bg-[#121418] p-6 transition-colors hover:border-[#3B82F6]/60"
              >
                <s.icon className="h-6 w-6 text-[#3B82F6]" strokeWidth={1.8} />
                <div className="mt-8 flex items-center justify-between gap-2">
                  <span className="font-display text-base font-bold text-white sm:text-lg">
                    {SHORT_NAMES[s.id] || s.title}
                  </span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-gray-600 transition-all group-hover:translate-x-1 group-hover:text-[#3B82F6]" />
                </div>
              </a>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-y border-white/10 bg-[#0E1013]" data-testid="store-perks-section">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
          <div className="grid gap-5 md:grid-cols-3">
            {PERKS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.1} className="h-full">
                <div className="spotlight-card h-full border border-[#262A35] bg-[#121418] p-7" data-testid={`store-perk-${i}`}>
                  <p.icon className="h-6 w-6 text-[#3B82F6]" strokeWidth={1.8} />
                  <h3 className="font-display mt-4 text-lg font-bold text-white">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-gray-400">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-12 text-center">
            <a
              href={STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="store-visit-button-bottom"
              className="font-mono2 group inline-flex items-center gap-3 bg-[#3B82F6] px-8 py-4 text-sm font-bold uppercase tracking-[0.25em] text-white transition-colors hover:bg-[#2563EB]"
            >
              Start Shopping
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </a>
            <p className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-500">
              <Phone className="h-4 w-4 text-[#3B82F6]" />
              Questions about a product? Call {CONTACT.phoneDisplay}
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
